import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { UserSubscriptionI } from "@seneca/subscription-service-types/out/models/UserSubscription";

import { getClient } from "services";

import { receiveSubscriptionProducts } from "seneca-common/features/subscriptions/features/subscription-products/state";
import { ReceivedSubscriptionProductType } from "seneca-common/features/subscriptions/state/types";

export const childrenSubscriptionsQueryKey = "children-subscriptions";

type ReceivedSubscriptions = {
  items: ChildSubscription[];
  count: number;
};

type Subscription = Pick<
  UserSubscriptionI,
  | "userId"
  | "subscriptionId"
  | "expiryDate"
  | "status"
  | "paymentStatus"
  | "managedBy"
  | "subscriptionProductId"
  | "stripeSubscriptionId"
>;

export type ChildSubscription = Subscription & {
  isActive: boolean;
  card: {
    brand: string;
    last4: string;
  };
  product: ReceivedSubscriptionProductType;
  addonProducts: ReceivedSubscriptionProductType[];
};

function useFetchChildrenSubscriptions(
  options: Pick<
    UseQueryOptions<ReceivedSubscriptions, unknown, ChildSubscription[]>,
    "select"
  >
) {
  const dispatch = useDispatch();
  return useQuery<ReceivedSubscriptions, unknown, ChildSubscription[]>({
    queryKey: [childrenSubscriptionsQueryKey],
    queryFn: async () => {
      const output = (await getClient(
        "parentsService"
      ).fetchChildrenSubscriptions()) as any as ReceivedSubscriptions;
      if (!(output instanceof Error)) {
        const subscriptionProducts = getSubscriptionProducts(output.items);
        dispatch(receiveSubscriptionProducts(subscriptionProducts));
      }

      return output;
    },
    staleTime: Infinity,
    retry: false,
    ...(options || {})
  });
}

export type QueryOptions = Pick<
  UseQueryOptions<ReceivedSubscriptions, unknown, ChildSubscription[]>,
  "enabled"
>;

function useQuerySelector(
  selector?: (data: ReceivedSubscriptions) => ChildSubscription[],
  otherOptions?: QueryOptions
) {
  return useFetchChildrenSubscriptions({
    select: selector,
    ...(otherOptions || {})
  });
}

export function useGetChildActiveSubscriptions(
  childId: string,
  options?: QueryOptions
) {
  return useQuerySelector(activeSubscriptions(childId), options);
}

const activeSubscriptions =
  (userId: string) => (data: ReceivedSubscriptions) => {
    return data?.items.filter(s => s.userId === userId && !!s.isActive);
  };

export function useGetChildrenActiveSubscriptions(options?: QueryOptions) {
  return useQuerySelector((data: ReceivedSubscriptions) => {
    return data?.items.filter(s => !!s.isActive);
  }, options);
}

function getSubscriptionProducts(subscriptions: ChildSubscription[]) {
  return subscriptions
    .reduce((subscriptionProducts, subscription) => {
      const mainProduct = subscription.product;
      const addonProducts = subscription.addonProducts;
      return subscriptionProducts.concat(mainProduct, addonProducts);
    }, [] as ReceivedSubscriptionProductType[])
    .filter(s => !!s);
}
